import type { StatusBarStyle } from 'expo-status-bar'
import { StatusBar } from 'expo-status-bar'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { BackHandler, Image, Keyboard, Platform, ScrollView, StyleSheet, View } from 'react-native'
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated'
import { BottomSheetModal } from '@gorhom/bottom-sheet'

import Menu from '../../../assets/mainHeader/Menu.png'
import { ActionModal } from '../../components'
import { CheatSheet } from '../../components/CheatSheet/CheatSheet'
import { useCheatSheet } from '../../components/CheatSheet/useCheatSheet'
import {
  DailyInspirationCommentSheet,
  InspirationSheetType
} from '../../components/dailyInspirationCommentSheet/DailyInspirationCommentSheet'
import { useDailyInspirationCommentSheet } from '../../components/dailyInspirationCommentSheet/useDailyInspirationCommentSheet'
import {
  DailyInspirationRecentResponseSheet,
  useDailyInspirationRecentResponseSheet
} from '../../components/DailyInspirationRecentResponsesSheet'
import { MainHeader } from '../../components/MainHeader/MainHeader'
import { ReportResponseSheet, useReportResponseSheet } from '../../components/ReportResponseSheet'
import { DS } from '../../design-system'
import { useActualHeight } from '../../hooks/use-actual-height'
import { translate } from '../../i18n'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import useFetchLastGameStats from '../game-statistics-screen/useFetchLastGameStats'
import { WelcomeModal } from '../join-premium/components/welcomeModal'
import { Accordian, AccordianType } from './Accordian'
import { DailyTask, DailyTaskList } from './DailyTask'
import { LiveEventBoard } from './LiveEventBoard'
import { RatingBoard } from './RatingBoard'
import { JoinPremiumCard } from './Sections/JoinPremium'
import styles from './styles'
import { UserWelcome } from './UserWelcome'
import { useRemoteConfig } from '../../../module/remote-config/useRemoteConfig'
import { SafeAreaView } from 'react-native-safe-area-context'
import { router, useFocusEffect } from 'expo-router'
import { useIsFocused } from '@react-navigation/native'
import { useDrawerStore } from '@/components/Drawer/Drawer'
import { usePollingQuery } from '@/hooks/usePollingQuery'

export enum GameOptions {
  join = 'Join Game',
  create = 'Create Game',
  practice = 'Practice',
  walkthrough = 'Open Hand Walkthrough'
}

export enum DiscussScreenSource {
  homeScreen = 'home_screen',
  shareModal = 'share_modal'
}

const HOME_BACKGROUND_IMAGES = [
  require('./assets/home.png'),
  require('./assets/home1.png'),
  require('./assets/home2.png'),
  require('./assets/home3.png')
]

const getHomeBackgroundImage = () => {
  const currentDate = new Date()
  const imageIndex = currentDate.getDate() % HOME_BACKGROUND_IMAGES.length
  return HOME_BACKGROUND_IMAGES[imageIndex]
}

const isWeb = Platform.OS === 'web'

export const HomeScreen = observer(() => {
  const isFocused = useIsFocused()
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const { open: handleOpenCheatSheet } = useCheatSheet()
  const { openDrawer } = useDrawerStore()
  const store = useStore()
  const HEIGHT = useActualHeight()
  const isSmallerDevice = HEIGHT < 670

  const {
    game: { teardown },
    memberships: {
      getMySubscription,
      newSubscription,
      clearNewSubscriptionState,
      justSubscribed,
      setJustSubscribed,
      isSubscribed,
      noSubscriptionOrCanceledOne,
      activeNewSubscriptionState
    },
    monday: { selectedEventData, numberOfDaysPassedPostEvent },
    session: {
      user,
      setLastVisitedTab,
      taskDay,
      firstName,
      getDailyInspirationalQuote,
      getDailyTask,
      dailyInspirationalQuoteData,
      addDailyInspirationResponses,
      getInspoReactionsUsingId,
      recentResponses,
      setPageNumber,
      setHasMore,
      getUserResponseCount
    },
    environment: { stage },
    analytics: { setEvent }
  } = useStore()

  const userInitial = firstName[0]
  const scrollViewRef = useRef<ScrollView | null>(null)
  const {
    dailyInspirationRecentResponseSheetRef,
    closeDailyInspirationRecentResponseSheet,
    openDailyInspirationRecentResponseSheet
  } = useDailyInspirationRecentResponseSheet()

  const { openDailyInspirationCommentSheet, dailyInspirationCommentSheetRef, closeDailyInspirationCommentSheet } =
    useDailyInspirationCommentSheet()

  const { reportResponseRef, closeReportResponseSheet, openReportResponseSheet } = useReportResponseSheet()

  const userId = user?._id.toString()
  const [isVisible, setIsVisible] = React.useState(false)
  const [isReponseLoading, setIsReponseLoading] = useState(false)
  const [rating, setRating] = React.useState(0)
  const [bottomSheetHeight, setBottomSheetHeight] = useState('58%')

  const memberShipScreenEnabled = useRemoteConfig((state) => state.tab_nav_config.enabledTabs.membershipScreen)
  const isDailyTasksEnabled = useRemoteConfig((state) => state.is_daily_tasks_enabled)
  const isDailyInspirationEnabled = useRemoteConfig((state) => state.is_daily_inspiration_enabled)
  const isLastGameStatsEnabled = useRemoteConfig((state) => state.is_last_game_stats_enabled)
  const isWelcomeMessageEnabled = useRemoteConfig((state) => state.is_home_welcome_message_enabled)
  const shouldShowUserWelcome = isWelcomeMessageEnabled || isDailyInspirationEnabled

  useEffect(() => {
    if (isFocused) {
      setLastVisitedTab('home')
    }
  }, [isFocused, setLastVisitedTab])

  const { userStats } = useFetchLastGameStats()

  React.useEffect(() => {
    if (recentResponses.length > 1) {
      const height = isSmallerDevice ? '72%' : '58%'
      setBottomSheetHeight(height)
    } else {
      const height = isSmallerDevice ? '35%' : '30%'
      setBottomSheetHeight(height)
    }
  }, [recentResponses])

  //Event for home screen
  React.useEffect(() => {
    console.log('Home Screen viewed')
    setEvent(events.homescreen.home_screen_viewed, {
      userId
    })
  }, [setEvent, userId])

  const fetchUserNotifications = useCallback(async () => {
    try {
      await Promise.allSettled([store.session.updateUserVisitCount(), store.session.getUserNotification()])
    } catch (error) {
      console.log('Error in getUserNotification', error)
    }
  }, [store.session])

  usePollingQuery('getMySubscription', getMySubscription, {
    refetchInterval: 5_000,
    enabled: !noSubscriptionOrCanceledOne && justSubscribed
  })

  useEffect(() => {
    if (justSubscribed && isSubscribed) {
      setJustSubscribed(false)
      activeNewSubscriptionState()
    }
  }, [justSubscribed, isSubscribed, setJustSubscribed, activeNewSubscriptionState])

  useEffect(() => {
    fetchUserNotifications()
  }, [fetchUserNotifications])

  useEffect(() => {
    const changeQuoteAndTaskList = async () => {
      await getDailyTask()
      await getDailyInspirationalQuote()
    }
    changeQuoteAndTaskList()
  }, [getDailyInspirationalQuote, getDailyTask, taskDay])

  const updateUserSegmentIdentity = useCallback(async () => {
    try {
      // Note: Update Segment identity if user logged in
      await store.session.getUserInfo()
    } catch (error) {
      console.log('Error in getUserNotification', error)
    }
  }, [store.session])

  useFocusEffect(
    React.useCallback(() => {
      if (!store.analytics.isSkillLevelAvailableInTraits) {
        updateUserSegmentIdentity()
      }
    }, [store.analytics.isSkillLevelAvailableInTraits, updateUserSegmentIdentity])
  )

  useFocusEffect(
    useCallback(() => {
      teardown()
    }, [teardown])
  )

  useFocusEffect(
    useCallback(() => {
      getMySubscription()
    }, [getMySubscription])
  )
  useFocusEffect(
    useCallback(() => {
      const backAction = () => {
        setIsVisible(true)
        return true
      }
      const backHandler = BackHandler.addEventListener('hardwareBackPress', backAction)
      return () => backHandler.remove()
    }, [])
  )

  const navToAuth = useCallback(() => {
    if (store.session) {
      store.session.logout()
    }
  }, [])

  useEffect(() => {
    if (newSubscription) {
      bottomSheetModalRef.current?.present()
      clearNewSubscriptionState()
    }
  }, [clearNewSubscriptionState, newSubscription])

  const onPressViewResponse = () => {
    // setEvent(events.dailyInspirationEvents.read_responses_tapped, {
    //   userId: userId,
    //   source: 'share_modal'
    // })
    setPageNumber(0)
    setHasMore(true)
    goToDiscussScreen(DiscussScreenSource.shareModal)
    // setTimeout(() => {
    //   navigation.navigate(DrawerRoutes.lobby, { screen: HomeRoutes.discussionScreen })
    // }, 0)
  }

  const submitResponse = async (response: string) => {
    Keyboard.dismiss()
    setIsReponseLoading(true)
    await addDailyInspirationResponses(response)
    await getInspoReactionsUsingId()
    setEvent(events.dailyInspirationEvents.response_submitted, {
      userId: userId
    })
    await getUserResponseCount()
    closeDailyInspirationCommentSheet()
    setIsReponseLoading(false)
    setBottomSheetHeight('58%')
    setTimeout(() => {
      openDailyInspirationRecentResponseSheet()
    }, 100)
  }

  const shouldShowLastGameStats = () => {
    if (!isLastGameStatsEnabled) {
      return false
    }
    if (!userStats) {
      return false
    }
    const rank = userStats.rank ?? 0
    return rank > 0
  }

  const goToDiscussScreen = (source: DiscussScreenSource) => {
    setEvent(events.dailyInspirationEvents.read_responses_tapped, {
      userId: userId,
      source
    })
    router.navigate('/lobby/discussion')
  }

  return (
    <Animated.View entering={FadeIn} style={{ flex: 1, backgroundColor: 'yellow' }}>
      <SafeAreaView style={styles.container}>
        {isFocused ? <StatusBar style={'dark' as StatusBarStyle} /> : null}
        <View
          id="background-svg"
          style={[
            StyleSheet.absoluteFill,
            {
              maxWidth: 600,
              marginHorizontal: isWeb ? 'auto' : undefined
            }
          ]}
        >
          <BackgroundDiamond />
        </View>
        <MainHeader hideLeftDefaultIcon rightIcon={Menu} rightPress={() => openDrawer()} />
        <ScrollView
          ref={scrollViewRef}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            paddingHorizontal: 24
          }}
          nestedScrollEnabled
        >
          <Animated.View entering={FadeInDown} style={styles.screenContentAnimatedView}>
            {shouldShowUserWelcome && (
              <View
                id="user-welcome"
                style={[
                  {
                    marginTop: 25,
                    paddingBottom: isDailyInspirationEnabled ? 25 : 0
                  },
                  containerStyles.webWrapper
                ]}
              >
                <UserWelcome
                  openDailyInspirationCommentSheet={openDailyInspirationCommentSheet}
                  goToDiscussScreen={() => goToDiscussScreen(DiscussScreenSource.homeScreen)}
                />
              </View>
            )}
            {numberOfDaysPassedPostEvent !== -1 && (
              <View id="live-event-board-container" style={containerStyles.webWrapper}>
                {numberOfDaysPassedPostEvent === 0 ? (
                  <LiveEventBoard
                    eventName={selectedEventData?.eventName ?? ''}
                    userFirstName={firstName}
                    handleOpenCheatSheet={handleOpenCheatSheet}
                  />
                ) : numberOfDaysPassedPostEvent > 1 ? (
                  <RatingBoard userFirstName={firstName} appRating={rating} setRating={setRating} />
                ) : null}
              </View>
            )}

            {isDailyTasksEnabled && (
              <View id="daily-task" style={containerStyles.webWrapper}>
                <DailyTask key="DailyTaskList.userFirstVisit" taskListType={DailyTaskList.userFirstVisit} />
              </View>
            )}

            {shouldShowLastGameStats() && (
              <Accordian
                key="game-accordian"
                scrollViewRef={scrollViewRef}
                title="Your last game"
                accordianType={AccordianType.gameType}
              />
            )}
            {/**
             * Task - https://peak6.atlassian.net/browse/PP-3369
             * from betina  - The Upcoming module should not show at all if there are 0 upcoming classes - which should be 100% of the time since we haven’t yet moved the group lessons into the app.
             * Hiding Upcoming event card
             */}
            {/* <Accordian key="personal-event-accordian" title="Upcoming" accordianType={AccordianType.upcomingEvent} /> */}
            {memberShipScreenEnabled && !isSubscribed && <JoinPremiumCard />}
            <ActionModal
              key={'logout'}
              onAction={navToAuth}
              onModalHide={() => setIsVisible(false)}
              onClose={() => setIsVisible(false)}
              message={translate('common.logoutConfirmation')}
              visible={isVisible}
            />
          </Animated.View>
        </ScrollView>
        <WelcomeModal stage={stage} bottomSheetModalRef={bottomSheetModalRef} />
        <DailyInspirationRecentResponseSheet
          bottomSheetModalRef={dailyInspirationRecentResponseSheetRef}
          onClose={closeDailyInspirationRecentResponseSheet}
          onPressViewResponse={onPressViewResponse}
          recentResponses={recentResponses}
          bottomSheetHeight={bottomSheetHeight}
        />
        <DailyInspirationCommentSheet
          inspirationMessage={dailyInspirationalQuoteData?.acf?.motivational_message ?? ''}
          userInitial={userInitial}
          onClose={closeDailyInspirationCommentSheet}
          ref={dailyInspirationCommentSheetRef}
          onSubmit={submitResponse}
          type={InspirationSheetType.DEFAULT}
          isReponseLoading={isReponseLoading}
          setIsReponseLoading={setIsReponseLoading}
        />
      </SafeAreaView>
    </Animated.View>
  )
})

const containerStyles = StyleSheet.create({
  webWrapper: !isWeb
    ? {}
    : {
        width: '100%',
        maxWidth: 600,
        alignSelf: 'center'
      }
})

const backgroundImageSource =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDY1IiBoZWlnaHQ9IjQ2NSIgdmlld0JveD0iMCAwIDQ2NSA0NjUiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxnIGNsaXAtcGF0aD0idXJsKCNwYWludDBfYW5ndWxhcl81MV8zMzlfY2xpcF9wYXRoKSIgZGF0YS1maWdtYS1za2lwLXBhcnNlPSJ0cnVlIj48ZyB0cmFuc2Zvcm09Im1hdHJpeCgwIDAuMjMyNSAwLjIzMjUgMCAyMzIuNSAyMzIuNSkiPjxmb3JlaWduT2JqZWN0IHg9Ii0xMDAwLjU4IiB5PSItMTAwMC41OCIgd2lkdGg9IjIwMDEuMTUiIGhlaWdodD0iMjAwMS4xNSI+PGRpdiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94aHRtbCIgc3R5bGU9ImJhY2tncm91bmQ6Y29uaWMtZ3JhZGllbnQoZnJvbSA5MGRlZyxyZ2JhKDI1NSwgNjEsIDIxMiwgMSkgMGRlZyxyZ2JhKDcyLCAxMywgMTQ3LCAxKSA3MC4yZGVnLHJnYmEoMTIzLCA0LCA2MSwgMSkgMTM1ZGVnLHJnYmEoMjU1LCAxMTMsIDMzLCAxKSAyMTZkZWcscmdiYSgyMzIsIDIyNSwgNTYsIDEpIDM2MGRlZyk7aGVpZ2h0OjEwMCU7d2lkdGg6MTAwJTtvcGFjaXR5OjEiPjwvZGl2PjwvZm9yZWlnbk9iamVjdD48L2c+PC9nPjxwYXRoIGQ9Ik0yMzIuNSA0NjVDMTcyLjQwOCAzNzQuMzM3IDk1LjAwOTggMjk2LjcxMSAwIDIzMi41Qzk1LjAwOTggMTY4LjI4OSAxNzIuMzU4IDkwLjY2MjggMjMyLjUgMEMyOTIuNTkyIDkwLjY2MjggMzY5Ljk5IDE2OC4yODkgNDY1IDIzMi41QzM2OS45OSAyOTYuNzExIDI5Mi42NDIgMzc0LjMzNyAyMzIuNSA0NjVaIiAvPgo8ZGVmcz4KPGNsaXBQYXRoIGlkPSJwYWludDBfYW5ndWxhcl81MV8zMzlfY2xpcF9wYXRoIj48cGF0aCBkPSJNMjMyLjUgNDY1QzE3Mi40MDggMzc0LjMzNyA5NS4wMDk4IDI5Ni43MTEgMCAyMzIuNUM5NS4wMDk4IDE2OC4yODkgMTcyLjM1OCA5MC42NjI4IDIzMi41IDBDMjkyLjU5MiA5MC42NjI4IDM2OS45OSAxNjguMjg5IDQ2NSAyMzIuNUMzNjkuOTkgMjk2LjcxMSAyOTIuNjQyIDM3NC4zMzcgMjMyLjUgNDY1WiIvPjwvY2xpcFBhdGg+PC9kZWZzPgo8L3N2Zz4K'

const BackgroundDiamond = () => {
  return (
    <Image
      source={!isWeb ? require('./assets/home-background-diamond.png') : { uri: backgroundImageSource }}
      width={465}
      height={465}
      style={{
        position: 'absolute',
        right: 0,
        marginTop: 138,
        marginRight: -182,
        width: 465,
        height: 465
      }}
    />
  )
}
